import debugFn from 'debug';
import { EventEmitter } from 'events';
import { Milliseconds, Seconds } from '../../../../types/date';
import { SegmentData } from '../SegmentProvider';
import { PlaybackTech, SegmentWithSource } from '../types';
import { IAudioEngine } from './IAudioEngine';

const debug = debugFn('audio:audio-src-engine');
debug.color = '#1cc0fc';

/**
 * Audio engine that uses standard HTMLAudioElement with src attribute.
 * Fallback for browsers without MediaSource API support (e.g., Safari iOS).
 *
 * Uses a double-buffering strategy with two audio elements:
 * - activeAudio: Currently playing element
 * - inactiveAudio: Preloading next segment for gapless transition
 *
 * When a segment ends, the elements are swapped (inactive becomes active).
 * This approach minimizes gaps between segments, though it's not as seamless
 * as MediaSourceEngine due to the inherent limitations of audio element switching.
 *
 * Note: Seeking precision depends on server support for HTTP Range requests.
 * Without Range support, seeks may land at the nearest buffered position.
 *
 * @emits timeupdate - Offset within current segment in milliseconds
 * @emits segmentEnded - Current segment's audio finished playing
 * @emits ended - All audio finished (no more segments queued)
 * @emits error - Playback error occurred
 */
export class AudioSrcEngine extends EventEmitter implements IAudioEngine {
  /** Currently playing audio element */
  private activeAudio: HTMLAudioElement | null = null;
  /** Preloading audio element for next segment */
  private inactiveAudio: HTMLAudioElement | null = null;
  /** Data for next segment (stored until advance) */
  private nextSegmentData: SegmentWithSource | null = null;
  private volume = 100;
  private currentSegmentDuration = 0;
  /** Guard flag to prevent double-advance from concurrent events */
  private isAdvancing = false;
  /** Mute state for both audio elements */
  private muted = false;

  /** Initializes both audio elements. Called lazily on first use. */
  async init(): Promise<void> {
    debug('initializing AudioSrcEngine');

    this.activeAudio = this.createAudioElement();
    this.inactiveAudio = this.createAudioElement();

    debug('AudioSrcEngine initialized');
  }

  /**
   * Creates an audio element and appends it to the DOM.
   * Elements are added to body to ensure they work across all browsers.
   */
  private createAudioElement(): HTMLAudioElement {
    const audio = document.createElement('audio');
    audio.preload = 'auto';
    audio.className = 'audio-player-src';
    audio.muted = this.muted;
    document.body.appendChild(audio);
    return audio;
  }

  /** Starts or resumes playback on the active audio element. */
  async play(): Promise<void> {
    if (!this.activeAudio) {
      await this.init();
    }

    debug('play');
    await this.activeAudio!.play();
  }

  /** Pauses the active audio element. */
  pause(): void {
    if (!this.activeAudio) return;
    debug('pause');
    this.activeAudio.pause();
  }

  /**
   * Clears all buffered audio and resets state.
   * Used when seeking to a different segment.
   */
  async reset(): Promise<void> {
    debug('reset');

    this.nextSegmentData = null;
    this.currentSegmentDuration = 0;

    this.clearAudioElement(this.activeAudio);
    this.clearAudioElement(this.inactiveAudio);
  }

  /**
   * Clears an audio element by removing its source and reloading.
   */
  private clearAudioElement(audio: HTMLAudioElement | null): void {
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }

  /**
   * Loads a segment into the active audio element.
   * @param data - Segment data with audio source URL
   */
  async enqueue(data: SegmentData): Promise<void> {
    if (!this.isSegmentWithSource(data)) {
      throw new Error('AudioSrcEngine requires audio source URL');
    }

    if (!this.activeAudio) {
      await this.init();
    }

    debug('enqueue segment: %s', data.audioSrc);
    this.activeAudio!.src = data.audioSrc;
    this.activeAudio!.load();
    this.currentSegmentDuration = data.segment.endTs - data.segment.startTs;

    this.setupAudioHandlers(this.activeAudio!);
  }

  /**
   * Attaches event handlers to an audio element.
   * Each handler checks if the element is still active before processing,
   * since events may fire after element swap during segment transitions.
   */
  private setupAudioHandlers(audio: HTMLAudioElement): void {
    audio.ontimeupdate = () => {
      // Ignore events from swapped-out element
      if (audio !== this.activeAudio) return;
      // audio.currentTime is already segment-relative (starts at 0 for each segment)
      const currentTimeMs = audio.currentTime * 1000;
      this.emit('timeupdate', currentTimeMs);
    };

    audio.onerror = () => {
      if (audio !== this.activeAudio) return;
      this.emit('error', new Error('Audio element error'));
    };

    audio.onended = () => {
      if (audio !== this.activeAudio) return;
      debug('audio ended naturally');
      this.emit('segmentEnded');
    };
  }

  /**
   * Preloads the next segment into the inactive audio element.
   * This enables near-gapless playback when advancing to next segment.
   * @param data - Segment data with audio source URL
   */
  async enqueueNext(data: SegmentData): Promise<void> {
    if (!this.isSegmentWithSource(data)) {
      throw new Error('AudioSrcEngine requires audio source URL');
    }

    debug('enqueue next segment: %s', data.audioSrc);
    this.nextSegmentData = data;

    // Start preloading in background while current segment plays
    if (this.inactiveAudio) {
      this.inactiveAudio.src = data.audioSrc;
      this.inactiveAudio.load();
    }
  }

  /**
   * Switches playback from current segment to the preloaded next segment.
   *
   * Performs a "swap" operation:
   * 1. Pauses current active element
   * 2. Swaps active ↔ inactive references
   * 3. Starts playback on newly active element (which has next segment preloaded)
   *
   * The old active element becomes inactive and can be reused for the next prefetch.
   */
  advanceToNextBuffer(): void {
    debug('advance to next buffer');

    // Guard: Both 'ended' event and SegmentsMediaPlayer can trigger advance
    if (this.isAdvancing) {
      debug('already advancing, skipping');
      return;
    }

    if (!this.nextSegmentData) {
      debug('no next segment data');
      this.emit('ended');
      return;
    }

    this.isAdvancing = true;

    // Stop current playback before swap
    if (this.activeAudio) {
      this.activeAudio.pause();
      this.activeAudio.currentTime = 0;
    }

    // Swap: inactive (preloaded) becomes active, active becomes available for next prefetch
    const temp = this.activeAudio;
    this.activeAudio = this.inactiveAudio;
    this.inactiveAudio = temp;

    this.currentSegmentDuration =
      this.nextSegmentData.segment.endTs - this.nextSegmentData.segment.startTs;
    this.nextSegmentData = null;

    this.setupAudioHandlers(this.activeAudio!);
    this.activeAudio!.play();

    this.isAdvancing = false;
  }

  /**
   * Seeks to a position within the current segment.
   *
   * Important: Seek precision depends on server HTTP Range request support.
   * - With Range support: Seeks to exact position
   * - Without Range support: Seeks to nearest buffered position (usually 0)
   *
   * The method waits for both metadata load and seek completion to ensure
   * the returned position is accurate.
   *
   * @param offsetMs - Target offset in milliseconds from segment start
   * @returns Actual offset where seek landed (may differ from target)
   */
  async seekToOffset(offsetMs: Milliseconds): Promise<Milliseconds> {
    if (!this.activeAudio) return 0 as Milliseconds;

    const seconds = (offsetMs / 1000) as Seconds;
    debug('seek to offset %dms (%ds)', offsetMs, seconds);

    // readyState < 1 means metadata not yet available (duration unknown)
    if (this.activeAudio.readyState < 1) {
      await this.waitForEvent(this.activeAudio, 'loadedmetadata');
    }

    this.activeAudio.currentTime = seconds;
    await this.waitForEvent(this.activeAudio, 'seeked');

    // Check actual position - may differ if server doesn't support Range requests
    const actualPosition = this.activeAudio.currentTime;
    const actualOffsetMs = (actualPosition * 1000) as Milliseconds;

    // Log warning if seek didn't land where expected (> 0.5s difference)
    if (Math.abs(actualPosition - seconds) > 0.5) {
      debug(
        'seek to %ds landed at %ds - server may not support Range requests',
        seconds,
        actualPosition,
      );
    }

    return actualOffsetMs;
  }

  /**
   * Waits for an event on an audio element.
   */
  private waitForEvent(audio: HTMLAudioElement, eventName: string): Promise<void> {
    return new Promise<void>((resolve) => {
      audio.addEventListener(eventName, () => resolve(), { once: true });
    });
  }

  /**
   * Sets volume on both audio elements.
   * @param value - Volume level from 0 (muted) to 100 (full)
   */
  setVolume(value: number): void {
    this.volume = value;
    // HTMLAudioElement uses 0-1 range, we use 0-100
    const normalizedVolume = value / 100;

    if (this.activeAudio) this.activeAudio.volume = normalizedVolume;
    if (this.inactiveAudio) this.inactiveAudio.volume = normalizedVolume;
  }

  /** Returns current volume level (0-100). */
  getVolume(): number {
    return this.volume;
  }

  /** Returns the playback technology this engine uses. */
  getTech(): PlaybackTech {
    return PlaybackTech.AUDIO_SRC;
  }

  /**
   * Releases all resources and removes audio elements from DOM.
   * Engine cannot be used after calling dispose.
   */
  dispose(): void {
    debug('dispose');

    this.disposeAudioElement(this.activeAudio);
    this.activeAudio = null;

    this.disposeAudioElement(this.inactiveAudio);
    this.inactiveAudio = null;

    this.nextSegmentData = null;
    this.removeAllListeners();
  }

  /**
   * Disposes of an audio element by cleaning up handlers and removing from DOM.
   */
  private disposeAudioElement(audio: HTMLAudioElement | null): void {
    if (!audio) return;
    audio.pause();
    audio.ontimeupdate = null;
    audio.onerror = null;
    document.body.removeChild(audio);
  }

  /** Type guard to ensure segment data includes audio source URL. */
  private isSegmentWithSource(data: SegmentData): data is SegmentWithSource {
    return 'audioSrc' in data && data.audioSrc !== undefined;
  }

  /**
   * Mutes both audio elements directly.
   */
  mute(): void {
    this.muted = true;
    if (this.activeAudio) this.activeAudio.muted = true;
    if (this.inactiveAudio) this.inactiveAudio.muted = true;
  }

  /**
   * Unmutes both audio elements directly.
   */
  unmute(): void {
    this.muted = false;
    if (this.activeAudio) this.activeAudio.muted = false;
    if (this.inactiveAudio) this.inactiveAudio.muted = false;
  }

  /**
   * Check if the engine is muted.
   */
  isMuted(): boolean {
    return this.muted;
  }
}
